// React
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import TabMenu from "../TabMenu/TabMenu";

// Helpers & Utilities
import { transformRoute, useTranslate } from "helpers";

// Services & API
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { getVehicleInsurancesAPI } from "services/api/calculations/vehicleinsurance/api";

// Types
import {
  VehicleInsuranceRow,
  VehicleInsuranceSearchFormValues,
} from "services/api/calculations/vehicleinsurance/types";
import {
  VehicleInsuranceSearchPageCmp,
  VehicleInsuranceSearchPageProps,
} from "./types";

// Constants
import {
  CALCULATION_VEHICLE_INSURANCE_CREATE_PAGE_TITLE,
  CALCULATION_VEHICLE_INSURANCE_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Custom Hooks
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Components
import VehicleInsuranceSearchFilters from "./components/VehicleInsuranceSearchFilters/VehicleInsuranceSearchFilters";
import VehicleInsuranceSearchResults from "./components/VehicleInsuranceSearchResults/VehicleInsuranceSearchResults";
import VehicleInsuranceEditCard from "../VehicleInsuranceEditCard/VehicleInsuranceEditCard";

// Routes
import { Routes } from "ui/modules/calculation/navigation";

const VehicleInsuranceSearchPage: VehicleInsuranceSearchPageCmp = (
  props: VehicleInsuranceSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleInsuranceRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [vehicleInsuranceId, setVehicleInsuranceId] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getVehicleInsurancesAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationVehicleInsuranceDetailPageRouter, {
        vehicleInsuranceId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setVehicleInsuranceId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: VehicleInsuranceSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setVehicleInsuranceId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_VEHICLE_INSURANCE_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_VEHICLE_INSURANCE_SEARCH_PAGE_TITLE.icon}
              iconColor={
                CALCULATION_VEHICLE_INSURANCE_SEARCH_PAGE_TITLE.iconColor
              }
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <VehicleInsuranceSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.vehicleinsurance.search.noresults"
              )}
            />
          ) : (
            <VehicleInsuranceSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_VEHICLE_INSURANCE_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <VehicleInsuranceEditCard
              vehicleInsuranceId={vehicleInsuranceId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

VehicleInsuranceSearchPage.route =
  Routes.CalculationVehicleInsuranceSearchPageRouter;

export default VehicleInsuranceSearchPage;
